import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, DirectionsRenderer, Marker, useJsApiLoader } from "@react-google-maps/api";
import styles from "./DeliveryMap.module.scss";

const DeliveryMap = ({ deliveryStops, driverLocation, shouldCalculateRoute, setShouldCalculateRoute, navigationActive, toast, setCurrentStop, currentStop, embedded, startNavigation, stopNavigation }) => {
    // Map and directions state
    const [map, setMap] = useState(null);
    const [directions, setDirections] = useState(null);
    const [directionsService, setDirectionsService] = useState(null);

    // Navigation state
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [currentLegIndex, setCurrentLegIndex] = useState(0);
    const [currentRouteSteps, setCurrentRouteSteps] = useState([]);
    const [lastAnnouncedStep, setLastAnnouncedStep] = useState(-1);
    const [distanceToNextManeuver, setDistanceToNextManeuver] = useState(null);
    const [currentInstruction, setCurrentInstruction] = useState("");
    const [nextInstruction, setNextInstruction] = useState("");
    const [isOffRoute, setIsOffRoute] = useState(false);
    const [eta, setEta] = useState(null);
    const [remainingDistance, setRemainingDistance] = useState(null);
    const [autoRecenter, setAutoRecenter] = useState(true);

    // Compass / device orientation state
    const [heading, setHeading] = useState(0);
    const [showOrientationPermission, setShowOrientationPermission] = useState(false);
    const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);

    // Refs
    const lastRouteCalculation = useRef(null);
    const routeCalculationCooldown = 30000;
    const navigationUpdateInterval = useRef(null);
    const offRouteCheckCount = useRef(0);
    const lastDriverLocation = useRef(null);
    const isProgrammaticZoom = useRef(false);
    const isProgrammaticHeading = useRef(false);

    // Voice settings
    const ENABLE_VOICE_NAVIGATION = true;

    // Navigation thresholds
    const DISTANCE_TO_ANNOUNCE_FAR = 400;
    const DISTANCE_TO_ANNOUNCE_NEAR = 100;
    const DISTANCE_TO_ANNOUNCE_NOW = 50;
    const DISTANCE_TO_NEXT_STEP = 30;
    const OFF_ROUTE_THRESHOLD = 50;
    const OFF_ROUTE_CHECK_THRESHOLD = 3;

    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: ["places", "geometry"],
    });

    // Initialize directions service
    useEffect(() => {
        if (isLoaded && window.google && window.google.maps) {
            setDirectionsService(new window.google.maps.DirectionsService());
        }
    }, [isLoaded]);

    // Device orientation / compass setup
    useEffect(() => {
        if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
            setShowOrientationPermission(true);
        } else if ("ondeviceorientationabsolute" in window || "ondeviceorientation" in window) {
            startCompassTracking();
        }

        return () => {
            stopCompassTracking();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOrientation = (event) => {
        let compassHeading = 0;
        if (event.webkitCompassHeading !== undefined && event.webkitCompassHeading !== null) {
            compassHeading = event.webkitCompassHeading;
        } else if (event.alpha !== null && event.alpha !== undefined) {
            compassHeading = 360 - event.alpha;
        }
        setHeading(compassHeading);
    };

    const startCompassTracking = () => {
        if ("ondeviceorientationabsolute" in window) {
            window.addEventListener("deviceorientationabsolute", handleOrientation, true);
        } else if ("ondeviceorientation" in window) {
            window.addEventListener("deviceorientation", handleOrientation, true);
        }
    };

    const stopCompassTracking = () => {
        window.removeEventListener("deviceorientationabsolute", handleOrientation, true);
        window.removeEventListener("deviceorientation", handleOrientation, true);
    };

    const requestOrientationPermission = () => {
        if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
            DeviceOrientationEvent.requestPermission()
                .then((response) => {
                    if (response === "granted") {
                        startCompassTracking();
                    }
                    setShowOrientationPermission(false);
                })
                .catch(() => {
                    setShowOrientationPermission(false);
                });
        }
    };

    // Keep map heading and tilt in sync
    useEffect(() => {
        if (map && navigationActive) {
            isProgrammaticHeading.current = true;
            map.setOptions({
                heading: heading,
                tilt: 65,
            });
            setTimeout(() => {
                isProgrammaticHeading.current = false;
            }, 100);
        } else if (map && !navigationActive) {
            map.setOptions({
                heading: 0,
                tilt: 0,
            });
        }
    }, [heading, navigationActive, map]);

    // Start/stop navigation tracking
    useEffect(() => {
        if (navigationActive && directions) {
            startNavigationTracking();
        } else {
            stopNavigationTracking();
        }

        return () => {
            stopNavigationTracking();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigationActive, directions]);

    // Handle off-route
    useEffect(() => {
        if (isOffRoute && navigationActive) {
            if (toast && toast.current) {
                toast.current.show({
                    severity: "warn",
                    summary: "Off Route",
                    detail: "Recalculating route...",
                    life: 2000,
                });
            }
            calculateRoute(true);
            setIsOffRoute(false);
            offRouteCheckCount.current = 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOffRoute]);

    // Auto-recenter map when driver location changes
    useEffect(() => {
        if (navigationActive && autoRecenter && map && driverLocation) {
            map.panTo(driverLocation);
            lastDriverLocation.current = driverLocation;
        }
    }, [driverLocation, navigationActive, autoRecenter, map]);

    // Detect manual map movement
    useEffect(() => {
        if (map && navigationActive) {
            const dragStartListener = map.addListener("dragstart", () => {
                console.log("👆 User dragging - disable auto-follow");
                setAutoRecenter(false);
            });

            const zoomChangedListener = map.addListener("zoom_changed", () => {
                if (isProgrammaticZoom.current) {
                    isProgrammaticZoom.current = false;
                    return;
                }
                console.log("🔍 User zoomed - disable auto-follow");
                setAutoRecenter(false);
            });

            const headingChangedListener = map.addListener("heading_changed", () => {
                if (isProgrammaticHeading.current) {
                    isProgrammaticHeading.current = false;
                    return;
                }
                if (navigationActive) {
                    const mapHeading = map.getHeading();
                    if (Math.abs(mapHeading - heading) > 10) {
                        console.log("🔄 User rotated map - disable auto-follow");
                        setAutoRecenter(false);
                    }
                }
            });

            return () => {
                if (window.google && window.google.maps && window.google.maps.event) {
                    window.google.maps.event.removeListener(dragStartListener);
                    window.google.maps.event.removeListener(zoomChangedListener);
                    window.google.maps.event.removeListener(headingChangedListener);
                }
            };
        }
    }, [map, navigationActive, heading]);

    const calculateRoute = async (forceRecalculate = false) => {
        if (!directionsService || !deliveryStops.length || !driverLocation) {
            console.log("Missing requirements for route calculation");
            return;
        }

        const now = Date.now();
        if (!forceRecalculate && lastRouteCalculation.current && now - lastRouteCalculation.current < routeCalculationCooldown) {
            console.log("Route calculation on cooldown");
            return;
        }

        setIsCalculatingRoute(true);

        try {
            const remainingStops = deliveryStops.filter((stop) => stop.status === "Not Delivered").sort((a, b) => a.stopNumber - b.stopNumber);

            if (remainingStops.length === 0) {
                setDirections(null);
                setCurrentRouteSteps([]);
                setCurrentInstruction("");
                setIsCalculatingRoute(false);
                return;
            }

            const firstStop = remainingStops[0];
            const firstDestination = { lat: firstStop.latitude, lng: firstStop.longitude };

            const waypoints = remainingStops.slice(1, -1).map((stop) => ({
                location: { lat: stop.latitude, lng: stop.longitude },
                stopover: true,
            }));

            const finalDestination =
                remainingStops.length > 1
                    ? {
                          lat: remainingStops[remainingStops.length - 1].latitude,
                          lng: remainingStops[remainingStops.length - 1].longitude,
                      }
                    : firstDestination;

            const request = {
                origin: driverLocation,
                destination: remainingStops.length === 1 ? firstDestination : finalDestination,
                waypoints: remainingStops.length > 1 ? [{ location: firstDestination, stopover: true }, ...waypoints] : [],
                travelMode: window.google.maps.TravelMode.DRIVING,
                optimizeWaypoints: false,
                drivingOptions: {
                    departureTime: new Date(),
                    trafficModel: window.google.maps.TrafficModel.BEST_GUESS,
                },
                provideRouteAlternatives: true,
                avoidHighways: false,
                avoidTolls: false,
                unitSystem: window.google.maps.UnitSystem.METRIC,
            };

            directionsService.route(request, (result, status) => {
                if (status === "OK") {
                    let selectedRoute = result.routes[0];
                    let shortestDistance = Infinity;

                    result.routes.forEach((route) => {
                        let totalDistance = 0;
                        route.legs.forEach((leg) => {
                            totalDistance += leg.distance.value;
                        });

                        if (totalDistance < shortestDistance) {
                            shortestDistance = totalDistance;
                            selectedRoute = route;
                        }
                    });

                    const optimizedResult = {
                        ...result,
                        routes: [selectedRoute],
                    };

                    setDirections(optimizedResult);
                    lastRouteCalculation.current = now;

                    extractRouteSteps(optimizedResult);

                    let totalDistance = 0;
                    let totalDuration = 0;

                    selectedRoute.legs.forEach((leg) => {
                        totalDistance += leg.distance.value;
                        totalDuration += leg.duration.value;
                    });

                    setRemainingDistance(totalDistance);
                    setEta(totalDuration);

                    if (currentStop && selectedRoute.legs[0]) {
                        const firstLeg = selectedRoute.legs[0];
                        setCurrentStop((prev) => ({
                            ...prev,
                            estimatedDistance: firstLeg.distance.value / 1000,
                            estimatedDuration: firstLeg.duration.value,
                        }));
                    }

                    if (toast && toast.current && forceRecalculate) {
                        toast.current.show({
                            severity: "success",
                            summary: "Route Calculated",
                            detail: `Distance: ${(totalDistance / 1000).toFixed(1)} km, Duration: ${Math.round(totalDuration / 60)} min`,
                            life: 3000,
                        });
                    }

                    offRouteCheckCount.current = 0;
                } else if (status === "ZERO_RESULTS") {
                    console.warn("No route found with waypoints, trying direct route");
                    calculateDirectRoute(driverLocation, firstDestination, forceRecalculate);
                } else {
                    console.error("Directions request failed:", status);
                    if (toast && toast.current && forceRecalculate) {
                        toast.current.show({
                            severity: "error",
                            summary: "Route Error",
                            detail: "Failed to calculate route. Please try again.",
                            life: 3000,
                        });
                    }
                }
                setIsCalculatingRoute(false);
            });
        } catch (error) {
            console.error("Error calculating route:", error);
            setIsCalculatingRoute(false);
        }
    };

    const calculateDirectRoute = (origin, destination, showToast = false) => {
        if (!directionsService) return;

        const request = {
            origin: origin,
            destination: destination,
            travelMode: window.google.maps.TravelMode.DRIVING,
            drivingOptions: {
                departureTime: new Date(),
                trafficModel: window.google.maps.TrafficModel.BEST_GUESS,
            },
            provideRouteAlternatives: true,
            avoidHighways: false,
            avoidTolls: false,
            unitSystem: window.google.maps.UnitSystem.METRIC,
        };

        directionsService.route(request, (result, status) => {
            if (status === "OK") {
                let selectedRoute = result.routes[0];
                let shortestDistance = Infinity;

                result.routes.forEach((route) => {
                    const distance = route.legs[0].distance.value;
                    if (distance < shortestDistance) {
                        shortestDistance = distance;
                        selectedRoute = route;
                    }
                });

                const optimizedResult = {
                    ...result,
                    routes: [selectedRoute],
                };

                setDirections(optimizedResult);
                extractRouteSteps(optimizedResult);

                const totalDistance = selectedRoute.legs[0].distance.value;
                const totalDuration = selectedRoute.legs[0].duration.value;

                setRemainingDistance(totalDistance);
                setEta(totalDuration);

                if (showToast && toast && toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Route Calculated",
                        detail: `Distance: ${(totalDistance / 1000).toFixed(1)} km, Duration: ${Math.round(totalDuration / 60)} min`,
                        life: 3000,
                    });
                }
            } else {
                console.error("Direct route calculation failed:", status);
            }
        });
    };

    const extractRouteSteps = (directionsResult) => {
        try {
            const route = directionsResult.routes[0];
            let allSteps = [];

            route.legs.forEach((leg) => {
                if (leg.steps) {
                    leg.steps.forEach((step) => {
                        allSteps.push(step);
                    });
                }
            });

            setCurrentRouteSteps(allSteps);
            setCurrentStepIndex(0);
            setCurrentLegIndex(0);
            setLastAnnouncedStep(-1);

            if (allSteps.length > 0) {
                const firstInstruction = cleanInstruction(allSteps[0].instructions);
                setCurrentInstruction(firstInstruction);

                if (allSteps.length > 1) {
                    setNextInstruction(cleanInstruction(allSteps[1].instructions));
                }

                if (ENABLE_VOICE_NAVIGATION) {
                    setTimeout(() => {
                        speakText(`Starting navigation. ${firstInstruction}`);
                        setLastAnnouncedStep(0);
                    }, 2000);
                }
            }
        } catch (error) {
            console.error("Error extracting route steps:", error);
        }
    };

    const startNavigationTracking = () => {
        if (navigationUpdateInterval.current) {
            clearInterval(navigationUpdateInterval.current);
        }

        navigationUpdateInterval.current = setInterval(() => {
            if (currentRouteSteps.length > 0 && driverLocation) {
                updateNavigationProgress();
            }
        }, 1000);
    };

    const stopNavigationTracking = () => {
        if (navigationUpdateInterval.current) {
            clearInterval(navigationUpdateInterval.current);
            navigationUpdateInterval.current = null;
        }

        if (ENABLE_VOICE_NAVIGATION && "speechSynthesis" in window) {
            window.speechSynthesis.cancel();
        }

        setCurrentStepIndex(0);
        setLastAnnouncedStep(-1);
        setDistanceToNextManeuver(null);
        setCurrentInstruction("");
        setNextInstruction("");
        offRouteCheckCount.current = 0;
        lastDriverLocation.current = null;
    };

    const calculateDistanceAlongPath = (currentPos, step) => {
        if (!step.path || step.path.length === 0) {
            const endPos = new window.google.maps.LatLng(step.end_location.lat(), step.end_location.lng());
            return window.google.maps.geometry.spherical.computeDistanceBetween(currentPos, endPos);
        }

        let totalDistance = 0;
        let nearestPointIndex = 0;
        let minDistance = Infinity;

        step.path.forEach((point, index) => {
            const distance = window.google.maps.geometry.spherical.computeDistanceBetween(currentPos, point);
            if (distance < minDistance) {
                minDistance = distance;
                nearestPointIndex = index;
            }
        });

        for (let i = nearestPointIndex; i < step.path.length - 1; i++) {
            totalDistance += window.google.maps.geometry.spherical.computeDistanceBetween(step.path[i], step.path[i + 1]);
        }

        return totalDistance;
    };

    const checkIfOffRoute = (currentPos, step) => {
        if (!step.path || step.path.length === 0) {
            return false;
        }

        let minDistance = Infinity;
        step.path.forEach((point) => {
            const distance = window.google.maps.geometry.spherical.computeDistanceBetween(currentPos, point);
            if (distance < minDistance) {
                minDistance = distance;
            }
        });

        return minDistance > OFF_ROUTE_THRESHOLD;
    };

    const updateNavigationProgress = () => {
        if (!currentRouteSteps.length || !driverLocation || !window.google?.maps?.geometry) return;

        try {
            const currentStep = currentRouteSteps[currentStepIndex];
            if (!currentStep) return;

            const currentPos = new window.google.maps.LatLng(driverLocation.lat, driverLocation.lng);

            if (checkIfOffRoute(currentPos, currentStep)) {
                offRouteCheckCount.current += 1;
                if (offRouteCheckCount.current >= OFF_ROUTE_CHECK_THRESHOLD) {
                    setIsOffRoute(true);
                    return;
                }
            } else {
                offRouteCheckCount.current = 0;
            }

            const distanceToStepEnd = calculateDistanceAlongPath(currentPos, currentStep);
            setDistanceToNextManeuver(distanceToStepEnd);

            const instruction = cleanInstruction(currentStep.instructions);
            const maneuverType = currentStep.maneuver || "";

            if (distanceToStepEnd <= DISTANCE_TO_ANNOUNCE_NOW && lastAnnouncedStep !== currentStepIndex * 3) {
                if (ENABLE_VOICE_NAVIGATION) {
                    speakText(instruction);
                    setLastAnnouncedStep(currentStepIndex * 3);
                }
            } else if (distanceToStepEnd <= DISTANCE_TO_ANNOUNCE_NEAR && lastAnnouncedStep !== currentStepIndex * 2) {
                if (ENABLE_VOICE_NAVIGATION && shouldAnnounceManeuver(maneuverType)) {
                    speakText(`In 100 meters, ${instruction}`);
                    setLastAnnouncedStep(currentStepIndex * 2);
                }
            } else if (distanceToStepEnd <= DISTANCE_TO_ANNOUNCE_FAR && lastAnnouncedStep !== currentStepIndex) {
                if (ENABLE_VOICE_NAVIGATION && shouldAnnounceManeuver(maneuverType)) {
                    speakText(`In 400 meters, ${instruction}`);
                    setLastAnnouncedStep(currentStepIndex);
                }
            }

            if (distanceToStepEnd < DISTANCE_TO_NEXT_STEP && currentStepIndex < currentRouteSteps.length - 1) {
                const newStepIndex = currentStepIndex + 1;
                setCurrentStepIndex(newStepIndex);

                const nextStep = currentRouteSteps[newStepIndex];
                const nextInstruction = cleanInstruction(nextStep.instructions);
                setCurrentInstruction(nextInstruction);

                if (newStepIndex + 1 < currentRouteSteps.length) {
                    setNextInstruction(cleanInstruction(currentRouteSteps[newStepIndex + 1].instructions));
                }
            }

            if (currentStepIndex === currentRouteSteps.length - 1 && distanceToStepEnd < DISTANCE_TO_NEXT_STEP) {
                if (ENABLE_VOICE_NAVIGATION && lastAnnouncedStep !== -999) {
                    speakText("You have arrived at your destination");
                    setLastAnnouncedStep(-999);
                }
            }
        } catch (error) {
            console.error("Error updating navigation progress:", error);
        }
    };

    const shouldAnnounceManeuver = (maneuverType) => {
        const importantManeuvers = ["turn-left", "turn-right", "turn-sharp-left", "turn-sharp-right", "turn-slight-left", "turn-slight-right", "ramp-left", "ramp-right", "merge", "fork-left", "fork-right", "ferry", "roundabout-left", "roundabout-right", "uturn-left", "uturn-right"];

        return importantManeuvers.includes(maneuverType) || maneuverType.includes("turn") || maneuverType.includes("exit") || maneuverType.includes("merge");
    };

    const speakText = (text) => {
        if (!ENABLE_VOICE_NAVIGATION || !("speechSynthesis" in window)) return;

        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 1;
        utterance.lang = "en-US";

        const voices = window.speechSynthesis.getVoices();
        const englishVoice = voices.find((voice) => voice.lang.startsWith("en") && (voice.name.includes("Google") || voice.name.includes("Microsoft") || voice.name.includes("Samantha")));
        if (englishVoice) {
            utterance.voice = englishVoice;
        }

        window.speechSynthesis.speak(utterance);
    };

    const cleanInstruction = (instruction) => {
        if (!instruction) return "";
        return instruction
            .replace(/<div[^>]*>/g, ", ")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/g, " ")
            .replace(/\s+/g, " ")
            .trim();
    };

    const formatDistance = (meters) => {
        if (!meters && meters !== 0) return "";
        if (meters < 1000) {
            return `${Math.round(meters)} m`;
        } else {
            return `${(meters / 1000).toFixed(1)} km`;
        }
    };

    const formatETA = (seconds) => {
        if (!seconds && seconds !== 0) return "";
        const minutes = Math.round(seconds / 60);
        if (minutes < 60) {
            return `${minutes} min`;
        } else {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            return `${hours}h ${remainingMinutes}m`;
        }
    };

    const getManeuverIcon = (instruction) => {
        const lowerInstruction = instruction.toLowerCase();

        if (lowerInstruction.includes("left")) return "↰";
        if (lowerInstruction.includes("right")) return "↱";
        if (lowerInstruction.includes("straight") || lowerInstruction.includes("continue")) return "↑";
        if (lowerInstruction.includes("u-turn")) return "⟲";
        if (lowerInstruction.includes("roundabout")) return "⟳";
        if (lowerInstruction.includes("merge")) return "↗";
        if (lowerInstruction.includes("exit")) return "↗";

        return "↑";
    };

    const recenterMap = () => {
        if (map && driverLocation) {
            setAutoRecenter(true);
            map.panTo(driverLocation);
            isProgrammaticZoom.current = true;
            map.setZoom(18);
            setTimeout(() => {
                isProgrammaticZoom.current = false;
            }, 100);
        }
    };

    // Calculate route when requested
    useEffect(() => {
        if (shouldCalculateRoute && navigationActive && directionsService && deliveryStops.length > 0 && driverLocation) {
            calculateRoute(true);
            setShouldCalculateRoute(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldCalculateRoute, navigationActive, directionsService, deliveryStops, driverLocation]);

    const center = navigationActive && driverLocation ? driverLocation : driverLocation || (currentStop ? { lat: currentStop.latitude, lng: currentStop.longitude } : { lat: 33.006257, lng: 73.15074 });

    if (!isLoaded) {
        return <div className={styles.mapLoading}>Loading map...</div>;
    }

    return (
        <div className={embedded ? styles.mapContainerEmbedded : styles.mapContainer}>
            <GoogleMap
                mapContainerStyle={
                    embedded
                        ? {
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              width: "100%",
                              height: "100%",
                          }
                        : {
                              position: "fixed",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              width: "100%",
                              height: "100vh",
                          }
                }
                center={center}
                zoom={navigationActive ? 18 : 13}
                onLoad={setMap}
                options={{
                    disableDefaultUI: true,
                    zoomControl: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    rotateControl: true,
                    gestureHandling: "greedy",
                    clickableIcons: false,
                    draggable: true,
                    scrollwheel: true,
                    disableDoubleClickZoom: false,
                    keyboardShortcuts: false,
                    mapTypeId: "roadmap",
                    rotateControlOptions: {
                        position: window.google?.maps?.ControlPosition?.RIGHT_BOTTOM,
                    },
                    styles: [
                        {
                            featureType: "poi",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }],
                        },
                        {
                            featureType: "poi.business",
                            stylers: [{ visibility: "off" }],
                        },
                        {
                            featureType: "transit",
                            elementType: "labels.icon",
                            stylers: [{ visibility: "off" }],
                        },
                    ],
                }}
            >
                {driverLocation && (
                    <Marker
                        position={driverLocation}
                        icon={{
                            path: "M 0,-30 L 8,0 L 0,6 L -8,0 Z",
                            fillColor: "#4285f4",
                            fillOpacity: 1,
                            strokeColor: "#ffffff",
                            strokeWeight: 2,
                            scale: 1.5,
                            rotation: navigationActive ? heading : 0,
                            anchor: new window.google.maps.Point(0, 0),
                        }}
                        title="Your Location"
                        zIndex={1000}
                        optimized={false}
                    />
                )}

                {deliveryStops.map((stop) => (
                    <Marker
                        key={stop._id}
                        position={{ lat: stop.latitude, lng: stop.longitude }}
                        label={{
                            text: stop.stopNumber.toString(),
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "14px",
                        }}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 18,
                            fillColor: stop.status === "Delivered" ? "#10B981" : stop.status === "Not Delivered" ? "#6366F1" : stop.status === "Incomplete" ? "#ef4444" : "#6B7280",
                            fillOpacity: 1,
                            strokeColor: "#ffffff",
                            strokeWeight: 3,
                        }}
                        title={`${stop.stopNumber}. ${stop.clientName}`}
                    />
                ))}

                {directions && (
                    <DirectionsRenderer
                        directions={directions}
                        options={{
                            polylineOptions: {
                                strokeColor: "#4285f4",
                                strokeOpacity: 0.9,
                                strokeWeight: 8,
                            },
                            suppressMarkers: true,
                            preserveViewport: navigationActive ? true : false,
                            suppressInfoWindows: true,
                            suppressBicyclingLayer: true,
                        }}
                    />
                )}
            </GoogleMap>

            {navigationActive && currentInstruction && (
                <>
                    <div className={styles.bottomNavCard}>
                        <div className={styles.navHeader}>
                            <div className={styles.etaInfo}>
                                <span className={styles.etaTime}>{formatETA(eta)}</span>
                                <span className={styles.etaDistance}>{formatDistance(remainingDistance)}</span>
                            </div>
                        </div>

                        <div className={styles.navInstruction}>
                            <div className={styles.maneuverIcon}>{getManeuverIcon(currentInstruction)}</div>
                            <div className={styles.instructionDetails}>
                                {distanceToNextManeuver && <div className={styles.instructionDistance}>{formatDistance(distanceToNextManeuver)}</div>}
                                <div className={styles.instructionText}>{currentInstruction}</div>
                            </div>
                        </div>

                        {nextInstruction && (
                            <div className={styles.nextInstruction}>
                                <span className={styles.thenText}>Then</span>
                                <span className={styles.nextText}>{nextInstruction}</span>
                            </div>
                        )}
                    </div>

                    <button className={`${styles.recenterButton} ${!autoRecenter ? styles.recenterActive : ""}`} onClick={recenterMap} title={autoRecenter ? "Auto-follow active" : "Click to re-center on your location"}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 8L12 2M12 2L9 5M12 2L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                        </svg>
                        {!autoRecenter && <span className={styles.recenterText}>Re-center</span>}
                    </button>

                    <button
                        className={styles.soundButton}
                        onClick={() => {
                            const speaking = window.speechSynthesis.speaking;
                            if (speaking) {
                                window.speechSynthesis.cancel();
                            }
                        }}
                    >
                        <i className="pi pi-volume-up" />
                    </button>
                </>
            )}

            {(startNavigation || stopNavigation) && (
                <button className={`${styles.navigationButton} ${navigationActive ? styles.stop : styles.start}`} onClick={navigationActive ? stopNavigation : startNavigation}>
                    {navigationActive ? <i className="pi pi-stop-circle" /> : <i className="pi pi-play-circle" />}
                </button>
            )}

            {isCalculatingRoute && (
                <div className={styles.calculatingOverlay}>
                    <div className={styles.calculatingSpinner}>
                        <i className="pi pi-spin pi-spinner" />
                        <span>Calculating route...</span>
                    </div>
                </div>
            )}

            {showOrientationPermission && (
                <div className={styles.permissionOverlay}>
                    <div className={styles.permissionDialog}>
                        <i className="pi pi-compass" />
                        <h3>Enable Compass</h3>
                        <p>Allow access to motion sensors for accurate navigation</p>
                        <button onClick={requestOrientationPermission}>Enable</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeliveryMap;
